import { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { userData } from "@signals"
import { pushRight } from "@app/@signals"
import { dsColorNew } from "@palette"
import { USER_ROUTES } from "@constants"
import { clearIrisProfile, irisProfile, irisProfileState, irisPrompt, loadIrisProfile } from "@signals/irisProfile"
import { pushIrisProfile, welcomeMarkdown, widgetLang } from "@utils/irisProfile"
import { clearAskContext } from "@utils/askIris/send"
import { IRIS_LOAD_FAILED } from "Layout/irisState"
import { destroyWidget, hasIris, irisIdentityKey, syncIrisStorage, WIDGET_SCRIPT_ID } from "@utils/irisStorage"

/**
 * Loads the rentacar-pms-agent chat widget script, dressed as Iris.
 *
 * IMPORTANT — access control (CU-869fmq2bf):
 *   The widget is loaded for the accounts with the `iris` feature (`featuresV2.iris`, on by
 *   default, `iris_off` in Admin > Users). This is the UX gate only; the server one is webback's
 *   `/api/iris/*` proxy, which checks the session and the same feature and writes the identity
 *   (`data-username` is ignored there).
 *
 * Behaviour:
 *   - No-op when the user is not logged in or has no `featuresV2.iris`.
 *   - When it applies, appends one <script> to <body> with the
 *     data-app / data-username attributes the widget expects, plus the
 *     look: no bubble of its own (the side menu's IrisLauncher opens it),
 *     a right drawer, the Iris mark and the app's blue.
 *   - On unmount, or when the user changes / logs out, removes the script AND the widget itself (its panel,
 *     `window.DSB`, its stored conversation) — always, without looking at who is logged in at that instant.
 *     Logout goes through `/logout`, a route outside the app layout, so this unmounts BEFORE `handleLogout`
 *     clears the session: a cleanup that compared identities saw "the same user" and left the open drawer, with
 *     the whole conversation, on /login (Curro, testing 2026-10-28). The logout page and the 401 of the session
 *     check (`useUserApp`) tear down too, as a net. Not the login page at mount: it also shows while a still valid
 *     session is being re-checked (`nextCheck` lasts 1 h, the session 7 days) and would wipe the user's own.
 *
 * The user's Iris profile (CU-869fkmt6r) — this is the only place that
 * talks to the widget about it:
 *   - The script is injected once `/api/ia-workspace/profile` answers (or
 *     fails), or after PROFILE_WAIT_MS if it hangs, so `data-welcome` knows
 *     whether to greet with "Set up Iris" (nothing of the user's saved) or
 *     leave the agent's own welcome. Read at mount only: no remount after
 *     the first Save — the launcher changes live, the welcome on the next
 *     page load (criterion 14c). The same wait gives `data-lang` the saved Iris language (Curro, 2026-10-28).
 *   - The saved profile is pushed with `DSB.setContext({ profile })` when it
 *     loads, on `dsb:ready` and after every Save (the signal changes);
 *     `{ profile: null }` before the script goes on logout (criterion 18b).
 *
 * The widget's stored conversation is per identity (CU-869fmq2bf): it keeps it in localStorage per agent and
 * re-sends it on every question, so before the script goes in, another identity (login or account on screen)
 * clears it (`syncIrisStorage`), and every teardown clears it and takes the widget off the page (`destroyWidget`:
 * its panel keeps the conversation on screen otherwise). The next identity starts from nothing; the same user
 * logging in again does too — a conversation never outlives its session in this browser.
 *
 * "Ask Iris" (CU-869fkdhjv phase 3, plan § 2.3): its evidence (`canvas_state`) is sticky in the widget,
 * so it is cleared here when the user leaves the screen (route change) and on logout / another user,
 * next to the profile. New data on the same screen is cleared by the screen itself.
 */
// Same origin, through webback's Iris proxy (CU-869fmq2bf): the widget derives its base from its own src, so every
// call it makes goes to `/api/iris/...` with the session cookie; no token ever reaches the browser
const AGENT_SRC       = '/api/iris/widget.js'
const SCRIPT_ELEMENT_ID = WIDGET_SCRIPT_ID
// The static mark (same drawing as @components/brand/IrisMark); `public/assets` is the
// tracked, served folder — `public/static` is build output and wiped by `npm run clean`
const IRIS_MARK_PATH = '/assets/iris.svg'

// A hung `/profile` must not leave the app without Iris: mount anyway after this long
const PROFILE_WAIT_MS = 8000
// The script injected and no `dsb:ready` after this long: Iris is "not available" (criterion 11, CU-869fkdhjv
// phase 3); a late `dsb:ready` still brings her back (`irisState.applyIrisEvent`)
const READY_WAIT_MS = 20000
const IRIS_SETTINGS_URL = USER_ROUTES.IRIS_PATH

// The host gave up on the script (criterion 11): marked on the script element, read by `useIrisState` at mount
const LOAD_FAILED_ATTR = 'data-load-failed'
export const widgetLoadFailed = () => document.getElementById(SCRIPT_ELEMENT_ID)?.getAttribute(LOAD_FAILED_ATTR) === 'true'

// The account has Iris (`featuresV2.iris`); the user defaults to the session's
export const isIrisUser = (user = userData.value) => hasIris(user)

// localStorage, or null when the browser blocks it (the helpers then do nothing)
const browserStorage = () => {
	try { return window.localStorage } catch { return null }
}

export default function PmsAgentWidget() {
	const rawUsername = userData.value?.username ?? ''
	const isAllowed = isIrisUser(userData.value)
	const identityKey = irisIdentityKey(userData.value)
	const profile = irisProfile.value
	const prompt = irisPrompt.value
	const { pathname } = useLocation()

	useEffect(() => {
		if (!isAllowed) {
			// Defensive: if a previous session as the allowed user left
			// the script behind, tear it down when a different user logs in —
			// and nothing of theirs travels any more (criterion 18b).
			clearAskContext(window.DSB)
			pushIrisProfile(window.DSB, null)
			clearIrisProfile()
			const stale = document.getElementById(SCRIPT_ELEMENT_ID)
			if (stale && stale.parentNode) stale.parentNode.removeChild(stale)
			// no Iris here: no widget and no stored conversation of anybody stays behind
			destroyWidget(window)
			syncIrisStorage(browserStorage(), null)
			return undefined
		}

		if (document.getElementById(SCRIPT_ELEMENT_ID)) return undefined

		// another identity than the stored conversation's: clear it, and drop a widget still on the page, before the
		// new one mounts
		if (syncIrisStorage(browserStorage(), identityKey)) destroyWidget(window)

		let cancelled = false
		let observer = null
		let stopWatch = null
		const mount = () => {
			if (cancelled || document.getElementById(SCRIPT_ELEMENT_ID)) return
			clearTimeout(wait)
			const injected = injectWidget(rawUsername)
			observer = injected.observer
			stopWatch = injected.stopWatch
		}
		const wait = setTimeout(mount, PROFILE_WAIT_MS)
		loadIrisProfile().finally(mount)

		return () => {
			cancelled = true
			clearTimeout(wait)
			// drop the profile and the last "Ask Iris" evidence before the widget goes, then forget them: the next
			// user starts clean
			clearAskContext(window.DSB)
			pushIrisProfile(window.DSB, null)
			clearIrisProfile()
			if (observer) observer.disconnect()
			if (stopWatch) stopWatch()
			pushRight.value = 0
			// the widget, its script and its conversation go — whoever `userData` says is logged in right now: on
			// `/logout` this runs before the session is cleared, and the panel must not survive that (see above)
			destroyWidget(window)
			syncIrisStorage(browserStorage(), null)
		}
	}, [isAllowed, rawUsername, identityKey])

	// The saved profile on every question: now, when the widget says it is ready (the script is async,
	// `window.DSB` may not exist yet), and whenever the signal changes (a Save, Account's refresh)
	useEffect(() => {
		if (!isAllowed || profile === null) return undefined
		const push = () => pushIrisProfile(window.DSB, profile, prompt)
		push()
		document.addEventListener('dsb:ready', push)
		return () => document.removeEventListener('dsb:ready', push)
	}, [isAllowed, profile, prompt])

	// "Ask Iris" evidence belongs to the screen it was asked on: another screen, no evidence (plan § 2.3)
	useEffect(() => {
		if (isAllowed) clearAskContext(window.DSB)
	}, [isAllowed, pathname])

	return null
}

// Appends the widget's <script>; returns the observer that mirrors its push margin and the stop of its load watch
function injectWidget(rawUsername) {
	const irisMarkUrl = `${window.location.origin}${IRIS_MARK_PATH}`
	const script = document.createElement('script')
	script.id           = SCRIPT_ELEMENT_ID
	script.src          = AGENT_SRC
	script.async        = true
	script.setAttribute('data-app', 'rentacarpms')
	script.setAttribute('data-username', rawUsername)  // pass verbatim, incl. case
	// Launcher: none — the side menu's IrisLauncher drives it through window.DSB
	script.setAttribute('data-launcher', 'none')
	script.setAttribute('data-layout', 'drawer')
	script.setAttribute('data-position', 'right')
	// `true`: the drawer narrows the page (margin on <html>) instead of covering it (Curro, 2026-10-21)
	script.setAttribute('data-push', 'true')
	script.setAttribute('data-title', 'Iris')
	script.setAttribute('data-icon-url', irisMarkUrl)
	script.setAttribute('data-avatar', irisMarkUrl)
	script.setAttribute('data-accent', dsColorNew.webBlue500)
	script.setAttribute('data-radius', 'round')

	// The agent's default drops the whole answer at once; reveal it progressively instead (guide § 7).

	// `data-stream="true"` would show tokens as they arrive, but the brain abandons drafts mid-way

	// (`answer_reset`, PRD-ia-workspace § 7k) — the typed-out finished answer is the honest one.

	script.setAttribute('data-typewriter', 'true')

	// First use: Iris's own welcome with the way to Settings › Iris, until something of the user's is saved
	const welcome = welcomeMarkdown({ profile: irisProfile.value, state: irisProfileState.value, settingsPath: IRIS_SETTINGS_URL })
	if (welcome) script.setAttribute('data-welcome', welcome)

	// The saved Iris language: the agent's welcome and starter suggestions come translated by its server. Not set →
	// no `data-lang`, Iris mirrors the user. Read at mount like the welcome; replies follow the pushed profile live
	const lang = widgetLang(irisProfile.value)
	if (lang) script.setAttribute('data-lang', lang)

	// The widget never loads (service down, URL blocked): no `dsb:ready` and no `dsb:error` ever come, so say it
	// ourselves — the launcher and every "Ask Iris" read "not available" instead of "starting" (criterion 11)
	const failed = () => {
		script.setAttribute(LOAD_FAILED_ATTR, 'true')
		document.dispatchEvent(new CustomEvent(IRIS_LOAD_FAILED))
	}
	const readyWait = setTimeout(failed, READY_WAIT_MS)
	const onReady = () => { clearTimeout(readyWait); script.removeAttribute(LOAD_FAILED_ATTR) }
	document.addEventListener('dsb:ready', onReady, { once: true })
	script.onerror = () => { clearTimeout(readyWait); failed() }
	const stopWatch = () => {
		clearTimeout(readyWait)
		document.removeEventListener('dsb:ready', onReady)
		script.onerror = null
	}

	document.body.appendChild(script)

	// `data-push` sets `margin-right` on <html> (drawer open, or resized by its grip); the
	// app's fixed header and bars span the viewport, so they would sit behind the drawer
	// (Curro, 2026-10-21). Mirror that margin into a signal the fixed elements subtract.
	const html = document.documentElement
	const sync = () => { pushRight.value = parseInt(html.style.marginRight, 10) || 0 }
	const observer = new MutationObserver(sync)
	observer.observe(html, { attributes: true, attributeFilter: ['style', 'class'] })
	sync()

	return { observer, stopWatch }
}
